import Link from '@/components/compat/Link';
import { useMemo, useState } from 'react';
import {
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  ExternalLink,
  History,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from 'lucide-react';
import { PainelConfirmDialog } from '@/components/painel/PainelFeedbackDialog';
import { PainelContentHistoryModal } from '@/components/painel/PainelContentHistoryModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { API_CATEGORY_DEFS } from '@/lib/powers-data';
import {
  painelTableShellClass,
  painelButtonPrimaryClass,
  painelButtonOutlineClass,
  painelSelectContentClass,
} from '@/components/painel/painel-styles';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { GsImage, ORBITAL_ICON_BOX_STYLE } from '@/components/help-center/latest-power-shared';
import { usePowersCms } from '@/components/painel/powers/powers-cms-context';
import { usePainelSaveFeedback } from '@/components/painel/PainelSaveFeedback';
import { usePainelSession } from '@/components/painel/rbac/painel-session-context';
import { usePainelText } from '@/components/painel/painel-texts-context';
import { sessionHasPermission } from '@/lib/painel-rbac';
import { logPainelContentActivity } from '@/lib/painel-activity-log';
import {
  PUBLICATION_STATUS_BADGE_CLASS,
  PUBLICATION_STATUS_LABELS,
} from '@/lib/cms-publication';
import { cn } from '@/lib/utils';
import {
  PainelCategoryFilterBar,
  PainelPowerCategoryBadge,
} from '@/components/painel/painel-category-visuals';
import { getPowerCategoryAccent } from '@/lib/power-category-theme';

const PAGE_SIZE = 50;
type SortOrder = 'desc' | 'asc';
type TypeFilter = 'all' | 'limited' | 'unlimited';

function formatUpdatedAt(iso: string) {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function PainelPowersPage() {
  const { powers, hydrated, deletePower, resetToMockSeed } = usePowersCms();
  const pageText = usePainelText('powers');
  const { showSaveSuccess } = usePainelSaveFeedback();
  const { session, role } = usePainelSession();
  const canCreatePowers = sessionHasPermission(session, 'powers.create');
  const canDeletePowers = sessionHasPermission(session, 'powers.delete');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [page, setPage] = useState(1);
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; label: string } | null>(null);
  const isSuperAdmin = role === 'super_admin';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = [...powers];

    if (categoryFilter) {
      list = list.filter((p) => p.category.toLowerCase() === categoryFilter);
    }
    if (typeFilter === 'limited') {
      list = list.filter((p) => p.limited);
    } else if (typeFilter === 'unlimited') {
      list = list.filter((p) => !p.limited);
    }
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.slug.toLowerCase().includes(q) ||
          String(p.id).includes(q)
      );
    }

    list.sort((a, b) => (sortOrder === 'asc' ? a.id - b.id : b.id - a.id));
    return list;
  }, [powers, search, categoryFilter, sortOrder, typeFilter]);

  const categoryFilterOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const power of powers) {
      const key = power.category.toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return API_CATEGORY_DEFS.filter((c) => counts.has(c.id)).map((c) => ({
      id: c.id,
      label: c.label,
      count: counts.get(c.id) ?? 0,
      accentHex: getPowerCategoryAccent(c.id).hex,
    }));
  }, [powers]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const deleteTarget = deleteSlug ? powers.find((p) => p.slug === deleteSlug) : null;
  const headerSelectTriggerClass = cn(
    'h-7 w-auto min-w-0 gap-1 border-0 bg-transparent px-1.5 py-0',
    'font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70',
    'shadow-none hover:bg-white/[0.06] hover:text-sky-100',
    'focus:ring-0 focus:ring-offset-0 [&>span]:line-clamp-1'
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <h1 className="font-poppins text-2xl font-bold tracking-tight text-white md:text-3xl">
            {pageText.title}
          </h1>
          <p className="mt-1 max-w-2xl font-inter text-sm text-slate-400">{pageText.description}</p>
        </div>
        <div className="flex shrink-0 flex-row flex-nowrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              resetToMockSeed();
              showSaveSuccess({
                title: 'Catálogo sincronizado',
                description: 'Os powers foram repostos com os dados do repositório.',
              });
            }}
            className={painelButtonOutlineClass}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Sincronizar com repositório
          </Button>
          {canCreatePowers ? (
            <Button
              asChild
              className={cn(painelButtonPrimaryClass, 'shadow-lg shadow-sky-950/30')}
            >
              <Link href="/painel/powers/new">
                <Plus className="mr-2 h-4 w-4" />
                Novo power
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar por nome, slug ou ID…"
            className={cn(painelInputClass, 'pl-9')}
          />
        </div>
        <p className="ml-auto font-inter text-xs text-slate-400">
          {hydrated ? `${filtered.length} powers` : 'Carregando…'}
        </p>
      </div>

      {hydrated && categoryFilterOptions.length > 0 ? (
        <PainelCategoryFilterBar
          options={categoryFilterOptions}
          value={categoryFilter}
          onChange={(id) => {
            setCategoryFilter(id);
            setPage(1);
          }}
          allCount={powers.length}
        />
      ) : null}

      <div className={painelTableShellClass}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse text-left font-inter text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.04]">
                <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Power
                </th>
                <th className="px-4 py-3">
                  <Select
                    value={sortOrder}
                    onValueChange={(value) => {
                      setSortOrder(value as SortOrder);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger
                      className={headerSelectTriggerClass}
                      aria-label="Ordenar por ID"
                    >
                      <SelectValue>
                        {sortOrder === 'asc' ? 'ID · Crescente' : 'ID · Decrescente'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className={painelSelectContentClass}>
                      <SelectItem value="desc">
                        <span className="inline-flex items-center gap-1.5">
                          <ArrowDownWideNarrow className="h-3.5 w-3.5" />
                          ID · Decrescente
                        </span>
                      </SelectItem>
                      <SelectItem value="asc">
                        <span className="inline-flex items-center gap-1.5">
                          <ArrowUpWideNarrow className="h-3.5 w-3.5" />
                          ID · Crescente
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </th>
                <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Categoria
                </th>
                <th className="px-4 py-3">
                  <Select
                    value={typeFilter}
                    onValueChange={(value) => {
                      setTypeFilter(value as TypeFilter);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger
                      className={headerSelectTriggerClass}
                      aria-label="Filtrar por tipo"
                    >
                      <SelectValue>
                        {typeFilter === 'limited'
                          ? 'Tipo · Limitado'
                          : typeFilter === 'unlimited'
                            ? 'Tipo · Ilimitado'
                            : 'Tipo · Todos'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className={painelSelectContentClass}>
                      <SelectItem value="all">Tipo · Todos</SelectItem>
                      <SelectItem value="limited">Tipo · Limitado</SelectItem>
                      <SelectItem value="unlimited">Tipo · Ilimitado</SelectItem>
                    </SelectContent>
                  </Select>
                </th>
                <th className="px-4 py-3 font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Atualizado
                </th>
                <th className="px-4 py-3 text-right font-poppins text-xs font-semibold uppercase tracking-wide text-sky-200/70">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {!hydrated ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                    Carregando catálogo…
                  </td>
                </tr>
              ) : pageItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                    Nenhum power encontrado.
                  </td>
                </tr>
              ) : (
                pageItems.map((p) => (
                  <tr key={p.slug} className="transition-colors hover:bg-white/[0.03]">
                    <td className="px-4 py-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <span
                          className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg ring-1 ring-white/10"
                          style={ORBITAL_ICON_BOX_STYLE}
                        >
                          <GsImage
                            code={p.slug}
                            className="flex h-full w-full items-center justify-center"
                            imgClassName="max-h-[26px] max-w-[26px] object-contain"
                            sizePx={40}
                          />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-poppins font-medium capitalize text-blue-50">
                            {p.name}
                          </p>
                          <p className="truncate font-mono text-xs text-slate-500">{p.slug}</p>
                          {p.publicationStatus && p.publicationStatus !== 'published' ? (
                            <span
                              className={cn(
                                'mt-1 inline-flex rounded-full px-2 py-0.5 font-poppins text-[10px] font-semibold uppercase tracking-wide ring-1',
                                PUBLICATION_STATUS_BADGE_CLASS[p.publicationStatus]
                              )}
                            >
                              {PUBLICATION_STATUS_LABELS[p.publicationStatus]}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-300">
                      {p.id > 0 ? p.id : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <PainelPowerCategoryBadge categoryId={p.category} />
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-semibold uppercase tracking-wide',
                          p.limited
                            ? 'bg-amber-500/15 text-amber-100 ring-1 ring-amber-400/25'
                            : 'bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/25'
                        )}
                      >
                        {p.limited ? 'Limitado' : 'Ilimitado'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-400">
                      {formatUpdatedAt(p.updatedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="rounded-xl text-blue-100 hover:bg-white/10 hover:text-white"
                          asChild
                          aria-label="Ver página pública"
                        >
                          <Link href={`/powers/${p.slug}`} target="_blank">
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        </Button>
                        {isSuperAdmin ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="rounded-xl text-blue-100 hover:bg-white/10 hover:text-white"
                            onClick={() => setHistoryTarget({ id: p.slug, label: p.name })}
                            aria-label="Histórico de alterações"
                          >
                            <History className="h-4 w-4" />
                          </Button>
                        ) : null}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="rounded-xl text-blue-100 hover:bg-white/10 hover:text-white"
                          asChild
                          aria-label="Editar power"
                        >
                          <Link href={`/painel/powers/${encodeURIComponent(p.slug)}/edit`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                        {canDeletePowers ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="rounded-xl text-rose-300 hover:bg-rose-500/15 hover:text-rose-100"
                            onClick={() => setDeleteSlug(p.slug)}
                            aria-label="Excluir power"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 ? (
        <div className="flex items-center justify-between gap-4">
          <p className="font-inter text-xs text-slate-400">
            Página {currentPage} de {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-xl border-white/15 bg-transparent text-blue-100 hover:bg-white/10"
            >
              Anterior
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-xl border-white/15 bg-transparent text-blue-100 hover:bg-white/10"
            >
              Próxima
            </Button>
          </div>
        </div>
      ) : null}

      <p className="font-inter text-xs text-slate-400/70">
        O catálogo é sincronizado com a API. Powers publicados aparecem no site público.
      </p>

      <PainelContentHistoryModal
        open={historyTarget !== null}
        onOpenChange={(open) => !open && setHistoryTarget(null)}
        resource="power"
        resourceId={historyTarget?.id ?? null}
        resourceLabel={historyTarget?.label ?? ''}
      />

      <PainelConfirmDialog
        open={deleteSlug !== null}
        onOpenChange={(o) => !o && setDeleteSlug(null)}
        title="Excluir power?"
        description={
          deleteTarget
            ? `“${deleteTarget.name}” será removido do catálogo gerido pelo painel.`
            : 'Esta ação não pode ser anulada.'
        }
        confirmLabel="Excluir"
        onConfirm={() => {
          if (deleteSlug) {
            deletePower(deleteSlug);
            logPainelContentActivity(session, {
              action: 'delete',
              resource: 'power',
              resourceId: deleteSlug,
              resourceLabel: deleteTarget?.name ?? deleteSlug,
            });
          }
          setDeleteSlug(null);
        }}
      />
    </div>
  );
}
